import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import { useFormikContext } from 'formik';
import { Card, Form, HorizontalSortableList } from 'components';
import EmptyStateIcon from 'components/common/EmptyStateIcon';
import placeholderImage from 'assets/img/covers/placeholder.jpg';
import SelectLooksModal from './SelectLooksModal';
import * as Ui from './LooksWidgetConfiguration.styles';

const LooksWidgetFormFields = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'widgets' });
  const { values, setFieldValue } = useFormikContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLooks, setSelectedLooks] = useState([]);

  // Sync selectedLooks from formik values (for edit mode)
  useEffect(() => {
    if (values.looks?.length > 0 && selectedLooks.length === 0) {
      setSelectedLooks(values.looks);
    }
  }, [values.looks, selectedLooks.length]);

  const handleConfirmLooks = (confirmedLooks) => {
    setSelectedLooks(confirmedLooks);
    setFieldValue('looks', confirmedLooks);
  };

  const handleRemoveLook = (uid) => {
    setSelectedLooks((prev) => prev.filter((l) => l.uid !== uid));
    setFieldValue(
      'looks',
      (values.looks || []).filter((l) => l.uid !== uid),
    );
  };

  const handleReorderLooks = (reordered) => {
    setSelectedLooks(reordered);
    setFieldValue('looks', reordered);
  };

  return (
    <Col md={12}>
      <Form.GroupHorizontal label={t('selectLooks')} required>
        {selectedLooks.length > 0 ? (
          <HorizontalSortableList
            items={selectedLooks}
            setItems={handleReorderLooks}
            keyExtractor={(item) => item.uid}
            renderItem={(look) => (
              <Ui.LookCard>
                <Ui.RemoveButton
                  type="button"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleRemoveLook(look.uid);
                  }}
                >
                  &times;
                </Ui.RemoveButton>
                <Ui.LookImage src={look.media_thumbnails[0] || placeholderImage} alt={look.name} />
                <Ui.LookInfo>{look.name && <Ui.LookName>{look.name}</Ui.LookName>}</Ui.LookInfo>
              </Ui.LookCard>
            )}
            listFooter={<Ui.AddButton onClick={() => setIsModalOpen(true)}>+</Ui.AddButton>}
          />
        ) : (
          <Ui.EmptyState onClick={() => setIsModalOpen(true)}>
            <EmptyStateIcon />
            <Ui.EmptyMessage>{t('noLooksSelected')}</Ui.EmptyMessage>
            <Ui.EmptySubMessage>{t('clickToAddLooks')}</Ui.EmptySubMessage>
          </Ui.EmptyState>
        )}
      </Form.GroupHorizontal>

      <SelectLooksModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmLooks}
        initialSelected={selectedLooks}
      />
    </Col>
  );
};

const LooksWidgetConfiguration = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'widgets' });

  return (
    <Card title={t('configuration')} subtitle={t('configurationSubtitle')} className="w-100">
      <Row>
        <LooksWidgetFormFields />
      </Row>
    </Card>
  );
};

export default {
  titleKey: 'types.looks',
  descriptionKey: 'types.looksDescription',
  defaultValues: { looks: [] },
  buildPayload: (values) => ({
    look_uids: (values.looks || []).map((l) => l.uid),
  }),
  isValid: (values) => values.looks?.length > 0,
  parseApiData: (widget) => ({
    looks: widget.looks || [],
  }),
  ConfigComponent: LooksWidgetConfiguration,
};
